import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { buildProductTrackingProperties } from '@/utils/tracking/productTrackingProperties';
import { useCallback, useRef } from 'react';

/**
 * Blue Light Glasses专题页神策埋点Hook
 * 对应Vue版本中blue-light-glasses/blueLightGlasses.vue的神策埋点功能
 */
export function useBlueLightGlassesTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const buildProductProperties = useCallback(
    (item: any) =>
      buildProductTrackingProperties(
        item,
        getPriceInfo(userInfo, item, currency),
        getPriceInfo(userInfo, item, { name: env.NEXT_PUBLIC_DEFAULT_CURRENCY }),
        currency,
      ),
    [currency, userInfo],
  );
  // 页面浏览状态跟踪
  const knowledgeTopicPageViewSended = useRef(false);
  const enterTimer = useRef(Date.now());

  /**
   * 神策埋点：防蓝光知识类专题页页面浏览
   * 对应Vue版本中的SDPageView函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPage_View
   * @EventDesc 专题页页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   * @Tapd 1010887
   */
  const trackKnowledgeTopicPageView = useCallback(() => {
    try {
      if (knowledgeTopicPageViewSended.current) return;
      knowledgeTopicPageViewSended.current = true;

      // 重置开始时间
      enterTimer.current = Date.now();
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'KnowledgeTopicPage_View',
          properties: {
            page_name: '防蓝光知识类专题页',
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('KnowledgeTopicPage_View tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：防蓝光知识类专题页模块曝光
   * 对应Vue版本中的SDPageMkExp函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageMkt_Exp
   * @EventDesc 专题页模块曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 模块名称,
   * }
   */
  const trackKnowledgeTopicPageMktExp = useCallback(
    (params: { mkt_name: string; [key: string]: any }) => {
      try {
        trackEvent(
          {
            eventName: 'KnowledgeTopicPageMkt_Exp',
            properties: {
              page_name: '防蓝光知识类专题页',
              ...params,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageMkt_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：防蓝光知识类专题页Banner曝光
   * 对应Vue版本中的SDBannerMkExp函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageBanner_Exp
   * @EventDesc 专题页Banner曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 模块名称,
   *   group_id: 分组ID,
   * }
   */
  const trackKnowledgeTopicPageBannerExp = useCallback(
    (params: { group_id: string | number; [key: string]: any }) => {
      try {
        trackEvent(
          {
            eventName: 'KnowledgeTopicPageBanner_Exp',
            properties: {
              page_name: '防蓝光知识类专题页',
              mkt_name: 'banner',
              group_id: `${params.group_id}`,
              ...params,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageBanner_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：防蓝光知识类专题页Banner点击
   * 对应Vue版本中的bannerClick函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageBanner_Click
   * @EventDesc 专题页Banner点击
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 模块名称,
   *   group_id: 分组ID,
   * }
   */
  const trackKnowledgeTopicPageBannerClick = useCallback(
    (params: { group_id: string | number; [key: string]: any }) => {
      try {
        trackEvent(
          {
            eventName: 'KnowledgeTopicPageBanner_Click',
            properties: {
              page_name: '防蓝光知识类专题页',
              mkt_name: 'banner',
              group_id: `${params.group_id}`,
              ...params,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageBanner_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：防蓝光知识类专题页商品坑位曝光
   * 对应Vue版本中的SDPageMkExp函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageProductPits_Exp
   * @EventDesc 专题页商品坑位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   pits_index: 坑位索引,
   *   mkt_name: 模块名称,
   *   product_id: 商品ID,
   *   shop_sku: 站点SKU,
   *   business_sku: 业务SKU,
   *   product_sku: 商品SKU,
   * }
   */
  const trackKnowledgeTopicPageProductExp = useCallback(
    (params: { item: any; index: number; mkt_name: string }) => {
      try {
        trackEvent(
          {
            eventName: 'KnowledgeTopicPageProductPits_Exp',
            properties: {
              page_name: '防蓝光知识类专题页',
              pits_index: params.index,
              mkt_name: params.mkt_name,
              ...buildProductProperties(params.item),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageProductPits_Exp tracking error:', error);
      }
    },
    [buildProductProperties, trackEvent],
  );

  /**
   * 神策埋点：防蓝光知识类专题页商品坑位点击
   * 对应Vue版本中的SDPageMktClick函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageProductPits_Click
   * @EventDesc 专题页商品坑位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   pits_index: 坑位索引,
   *   mkt_name: 模块名称,
   *   product_id: 商品ID,
   *   shop_sku: 站点SKU,
   *   business_sku: 业务SKU,
   *   product_sku: 商品SKU,
   * }
   */
  const trackKnowledgeTopicPageProductClick = useCallback(
    (params: { item: any; index: number; mkt_name: string }) => {
      try {
        trackEvent(
          {
            eventName: 'KnowledgeTopicPageProductPits_Click',
            properties: {
              page_name: '防蓝光知识类专题页',
              pits_index: params.index,
              mkt_name: params.mkt_name,
              ...buildProductProperties(params.item),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageProductPits_Click tracking error:', error);
      }
    },
    [buildProductProperties, trackEvent],
  );

  return {
    // 页面级埋点
    trackKnowledgeTopicPageView,
    // Banner级埋点
    trackKnowledgeTopicPageBannerExp,
    trackKnowledgeTopicPageBannerClick,
    // 模块级埋点
    trackKnowledgeTopicPageMktExp,
    // 商品级埋点
    trackKnowledgeTopicPageProductExp,
    trackKnowledgeTopicPageProductClick,
  };
}
